import { AdSlotType } from '../advertising/AdTypes';
import { RENDERING_CONFIG, type FogScale } from './rendering.config';

/**
 * Look of a slot type that differs from the fence banners – e.g. the ad gantry over the road,
 * which has to read from far away (AdSurfacePresenter).
 */
export interface AdSurfaceStyle {
  /** Self-illumination, replaces `bannerGlow` (floodlit banner: readable against the low sun and at night). */
  readonly glow: number;
  /**
   * Own linear fog band of the surface as multiples of the scene fog start/end (day and night
   * zones alike). Start > 1 keeps the creative legible further than the track around it; the end
   * stays just beyond the distance at which its module spawns (LevelConfig.segmentsAhead), so the
   * banner emerges from the haze instead of popping in.
   */
  readonly fogScale: FogScale;
  /** Anisotropic filtering of the creative texture (Babylon default 4). */
  readonly anisotropicFilteringLevel: number;
  /** Width of a generated placeholder creative (pixels) – replaces `placeholderTextureWidth`. */
  readonly placeholderTextureWidth: number;
}

export interface AdvertisingConfig {
  readonly enabled: boolean;
  /** Visibility sampling interval (seconds) – no need to test every frame. */
  readonly visibilitySampleInterval: number;
  /** A slot must be continuously visible this long to count as one impression (seconds). */
  readonly minVisibleTimeForImpression: number;
  /** Minimum share of the screen height the slot must cover to count as visible. */
  readonly minScreenHeightFraction: number;
  /** Slots further away than this are never counted as visible (meters). */
  readonly maxVisibleDistance: number;
  /** Texture resolution of generated placeholder creatives (pixels, width). */
  readonly placeholderTextureWidth: number;
  /**
   * Share of ad slots that carry a banner (0..1) – the rest show the bare
   * fence/carrier, like a real site. Decided per slot instance, deterministic.
   */
  readonly slotFillRate: number;
  /** Self-illumination of printed banners so they stay readable in shadow (0 = purely lit). */
  readonly bannerGlow: number;
  /**
   * Polygon offset of every ad surface in decal levels (rendering/decals.ts). The planes hang a few
   * centimetres in front of fences, walls and scaffolds – far away the depth buffer can no longer
   * tell them apart and the banner flickers (z-fighting). 1 = like a Blender `_DECAL` material.
   */
  readonly depthOffsetLevel: number;
  /**
   * Ad manifest: campaigns with JPEG/PNG creatives per slot format (docs/advertising/AD_SYSTEM.md).
   * A static file now; the ad backend later serves the same JSON under its own URL.
   */
  readonly manifestUrl: string;
  /** The start screen waits at most this long for the manifest, then uses the house ads (ms). */
  readonly manifestTimeoutMs: number;
  /** Slot types with their own surface look (all others: matte fence banner with `bannerGlow`). */
  readonly surfaceStyles: Readonly<Partial<Record<AdSlotType, AdSurfaceStyle>>>;
  /**
   * Ad backend per host (ructec portal, Edge Function `spiel-werbung`): serves the booked campaigns in the manifest
   * format and receives one report per run with the bookings seen in it (AdReporter). bauralley.com → live portal,
   * stage.bauralley.com and localhost → staging; hosts not listed keep the static manifest and report nothing.
   */
  readonly backendByHost: Readonly<Record<string, string>>;
}

/** Origins of all configured ad backends – the only places booked posters may be loaded from. */
export function adImageOrigins(config: AdvertisingConfig): string[] {
  return [...new Set(Object.values(config.backendByHost).map((u) => new URL(u).origin))];
}

/** Ad backend of this host, or null (then the static manifest is used and nothing is reported). */
export function adBackendUrl(config: AdvertisingConfig, hostname: string): string | null {
  return config.backendByHost[hostname] ?? null;
}

export const ADVERTISING_CONFIG: AdvertisingConfig = {
  enabled: true,
  slotFillRate: 0.45,
  bannerGlow: 0.18,
  depthOffsetLevel: 1,
  manifestUrl: '/ads/campaigns.json',
  manifestTimeoutMs: 4000,
  visibilitySampleInterval: 0.1,
  // a view = the banner was on screen for at least 2 s (booking rule ructec 04.10.2026; 2 instead of 3 s so the ad gantry –
  // ~35 m = 2.2 s in view at 16 m/s – counts at top speed too, user: "ja auch bei Hochgeschwindigkeit")
  minVisibleTimeForImpression: 2,
  backendByHost: {
    // live launch 2026-10-08 (ructec): bauralley.com talks to the live portal database
    'bauralley.com': 'https://macigkxnscddyaytybol.supabase.co/functions/v1/spiel-werbung',
    'www.bauralley.com': 'https://macigkxnscddyaytybol.supabase.co/functions/v1/spiel-werbung',
    'stage.bauralley.com': 'https://pasmhdxlsqrmomjwbzhz.supabase.co/functions/v1/spiel-werbung',
    localhost: 'https://pasmhdxlsqrmomjwbzhz.supabase.co/functions/v1/spiel-werbung',
  },
  minScreenHeightFraction: 0.04,
  maxVisibleDistance: 60,
  placeholderTextureWidth: 512,
  surfaceStyles: {
    // Ad gantry (SEG_AD_GANTRY_40M): floodlit, same far fog band as its steel frame (rendering.config landmarkFog).
    [AdSlotType.GantryBanner]: {
      glow: 0.35,
      fogScale: RENDERING_CONFIG.landmarkFog.scale,
      anisotropicFilteringLevel: 8,
      placeholderTextureWidth: 1024,
    },
  },
};
